import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    BASE_URL,
    DailyPoint,
    DeliveryRow,
    HealthResponse,
    REASON_LABELS,
    STATUS_LABELS,
    StatsResponse,
    formatDay,
    formatTime,
} from './shared'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const HEALTH_INTERVAL_MS = 20000
const HISTORY_LENGTH = 30

const DOCUMENTS = [
    {
        title: 'FFJ Payload Gaps (v5)',
        description:
            'What FairClaim Services must still send for FFJ accepts: ≥1 signed AOD, and no previous_searches. Print to PDF from the browser.',
        url: '/docs/ffj-payload-gaps.html',
    },
    {
        title: 'Client Acceptance Criteria',
        description: 'Business-facing criteria pack: every rule a client must meet, separated by concern.',
        url: '/docs/ffj-acceptance-criteria.html',
    },
    {
        title: 'Client Delivery API Specification',
        description:
            'Technical reference for the FFJ webhook. FairClaim Services v5 is normalised, then funding-batch gates run.',
        url: '/docs/ffj-api-spec.html',
    },
]

// The acceptance-criteria document pulls the approved lender list live from
// the case-delivery-app, so tell it which API root to use.
const docUrl = (url: string) => `${url}?api=${encodeURIComponent(BASE_URL)}`

const openDocAsPdf = (url: string) => {
    const win = window.open(docUrl(url), '_blank')
    if (!win) return
    win.addEventListener('load', () => {
        // Wait for the live lender list (data-lenders-loaded) before printing,
        // capped so a slow API never blocks the export.
        const startedAt = Date.now()
        const tryPrint = () => {
            let loaded = false
            try {
                loaded = win.document.documentElement.getAttribute('data-lenders-loaded') === '1'
            } catch {
                loaded = true
            }
            if (loaded || Date.now() - startedAt > 6000) {
                setTimeout(() => win.print(), 300)
                return
            }
            setTimeout(tryPrint, 150)
        }
        tryPrint()
    })
}

const GATES = [
    { name: 'Data validation', detail: 'v5 normalise + schema' },
    { name: 'Lender approval', detail: 'Approved defendant list' },
    { name: 'Credit / industry search', detail: 'previous_searches + soft checks' },
    { name: 'Duplicates', detail: 'Client / claim collisions' },
    { name: 'Client eligibility', detail: 'Valuation gates + £1,700 min' },
    { name: 'Signed evidence', detail: '≥1 CFA + LOA + AOD' },
    { name: 'E-signature', detail: 'Valid PNG + audit trail' },
    { name: 'AML & identity', detail: 'From v5 aml + credit_search' },
]

const CRITERIA: { title: string; items: string[] }[] = [
    {
        title: 'Data validation',
        items: [
            'Full name, title, DOB, email and phone all required and well-formed',
            'Complete UK address with valid postcode',
            'At least one agreement per client',
            'Every agreement: lender, start date, term ≥5 months, amounts, status and account ID',
            'Required client artefacts: credit report PDF, AML PDF, signature PNG',
        ],
    },
    {
        title: 'Lender approval',
        items: [
            'Every agreement\u2019s lender must match the approved lender list',
            'Names are normalised before matching',
            'One unapproved lender rejects the whole delivery',
        ],
    },
    {
        title: 'Client eligibility',
        items: [
            'Per-agreement valuation checks (dates, amounts, term, repayment band, age)',
            'Cumulative claim quantum must reach £1,700',
            'Valued with 10% commission and 8% statutory interest, 14 months after signing',
        ],
    },
    {
        title: 'Documents',
        items: [
            'At least one signed CFA, one LOA, and one AOD required as client-level evidence',
            'Unique per-claim CFA/LOA/AOD for additional claims are generated by Sentinel after acceptance',
            'Credit report & AML PDFs required at client level and archived to S3',
        ],
    },
    {
        title: 'E-signature',
        items: [
            'Signature must be a valid base64-encoded PNG',
            'Audit trail required: IP address and device or browser',
            'Signing timestamp must be valid and not in the future',
        ],
    },
    {
        title: 'AML & identity',
        items: [
            'Runs in real time when credit_report_json is delivered',
            'Identity check must be a pass, and the searched name must match',
            'No more than 4 CCJs; any IVA or bankruptcy rejects the client',
            'Failures return 422 with reason code aml_check_failed and the exact checks that failed',
        ],
    },
]

interface HealthSample {
    ok: boolean
    latency: number
    at: string
}

interface DayDetail {
    day: string
    loading: boolean
    rows: (DeliveryRow & { outcome: 'accepted' | 'rejected' })[]
    error?: string
}

export const OverviewTab = () => {
    const navigate = useNavigate()
    const [health, setHealth] = useState<HealthResponse | null>(null)
    const [healthError, setHealthError] = useState<string | null>(null)
    const [history, setHistory] = useState<HealthSample[]>([])
    const [lastChecked, setLastChecked] = useState<Date | null>(null)
    const [stats, setStats] = useState<StatsResponse | null>(null)
    const [days, setDays] = useState(7)
    const [dayDetail, setDayDetail] = useState<DayDetail | null>(null)
    const [lenders, setLenders] = useState<string[]>([])
    const [showLenders, setShowLenders] = useState(false)
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const checkHealth = useCallback(async () => {
        const startedAt = Date.now()
        try {
            const response = await api.post(`${BASE_URL}/api/ffj-health`, {})
            const data: HealthResponse = response.data
            setHealth(data)
            setHealthError(null)
            setHistory((h) => [...h, { ok: data.ok, latency: Date.now() - startedAt, at: data.timestamp }].slice(-HISTORY_LENGTH))
        } catch (error: any) {
            setHealth(null)
            setHealthError(error?.response?.data?.message || error?.message || 'Health check request failed')
            setHistory((h) => [...h, { ok: false, latency: Date.now() - startedAt, at: new Date().toISOString() }].slice(-HISTORY_LENGTH))
        } finally {
            setLastChecked(new Date())
        }
    }, [])

    useEffect(() => {
        checkHealth()
        pollRef.current = setInterval(() => {
            if (!document.hidden) checkHealth()
        }, HEALTH_INTERVAL_MS)
        return () => {
            if (pollRef.current) clearInterval(pollRef.current)
        }
    }, [checkHealth])

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-api-stats`, { days })
                if (response.data?.success) setStats(response.data)
            } catch {
                /* chart simply stays empty; optics tab surfaces stats errors */
            }
        }
        fetchStats()
    }, [days])

    useEffect(() => {
        const fetchLenders = async () => {
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-approved-lenders`, {})
                if (response.data?.success) setLenders(response.data.lenders)
            } catch {
                /* lender list section simply stays collapsed-empty */
            }
        }
        fetchLenders()
    }, [])

    const openDayDetail = useCallback(async (point: DailyPoint) => {
        setDayDetail({ day: point.day, loading: true, rows: [] })
        try {
            const [acceptedRes, rejectedRes] = await Promise.all([
                api.post(`${BASE_URL}/api/ffj-deliveries`, { view: 'accepted', date: point.day, pageSize: 100 }),
                api.post(`${BASE_URL}/api/ffj-deliveries`, { view: 'rejected', date: point.day, pageSize: 100 }),
            ])
            const rows = [
                ...(acceptedRes.data?.rows ?? []).map((r: DeliveryRow) => ({ ...r, outcome: 'accepted' as const })),
                ...(rejectedRes.data?.rows ?? []).map((r: DeliveryRow) => ({ ...r, outcome: 'rejected' as const })),
            ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            setDayDetail({ day: point.day, loading: false, rows })
        } catch (error: any) {
            setDayDetail({
                day: point.day,
                loading: false,
                rows: [],
                error: error?.response?.data?.message || 'Could not load the day breakdown',
            })
        }
    }, [])

    const chartData = useMemo(() => {
        const daily = stats?.daily ?? []
        return {
            labels: daily.map((d) => d.day.slice(5)),
            datasets: [
                {
                    label: 'Received',
                    data: daily.map((d) => d.received),
                    borderColor: '#1a1a2e',
                    backgroundColor: 'rgba(26, 26, 46, 0.06)',
                    fill: true,
                    tension: 0.25,
                },
                {
                    label: 'Accepted',
                    data: daily.map((d) => d.accepted),
                    borderColor: '#0b6a3c',
                    backgroundColor: 'transparent',
                    tension: 0.25,
                },
                {
                    label: 'Rejected',
                    data: daily.map((d) => d.rejected),
                    borderColor: '#a4262c',
                    backgroundColor: 'transparent',
                    tension: 0.25,
                },
            ],
        }
    }, [stats])

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index' as const, intersect: false },
            plugins: {
                legend: { position: 'top' as const },
                tooltip: {
                    callbacks: {
                        title: (items: any[]) => {
                            const idx = items[0]?.dataIndex
                            const point = stats?.daily[idx]
                            return point ? formatDay(point.day) : ''
                        },
                        footer: (items: any[]) => {
                            const idx = items[0]?.dataIndex
                            const point = stats?.daily[idx]
                            return point ? `${point.agreements} agreements · click for breakdown` : ''
                        },
                    },
                },
            },
            scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
            onClick: (_event: any, elements: any[]) => {
                const idx = elements[0]?.index
                const point = stats?.daily[idx]
                if (point) openDayDetail(point)
            },
        }),
        [stats, openDayDetail]
    )

    const latencyLabel = health
        ? `${Math.max(health.checks.leadsDb.latency_ms, health.checks.funderDb.latency_ms)}ms`
        : '—'

    return (
        <div>
            <div className={styles.infoGrid}>
                <div className={styles.healthCard}>
                    <div className={styles.healthHeader}>
                        <h4>Live status</h4>
                        <span
                            className={`${styles.healthBadge} ${
                                healthError ? styles.healthDown : health?.ok ? styles.healthUp : styles.healthUnknown
                            }`}
                        >
                            <span className={styles.healthDot} />
                            {healthError ? 'OFFLINE' : health?.ok ? 'ONLINE' : 'CHECKING…'}
                        </span>
                    </div>
                    <p className={styles.healthSub}>
                        Production webhook service — checked every 20 seconds. A healthy response proves the API is up
                        and able to receive cases.
                    </p>
                    <div className={styles.healthChecks}>
                        <div>
                            <span className={styles.healthCheckName}>Service</span>
                            <span className={healthError ? styles.checkBad : styles.checkGood}>
                                {healthError ? 'Unreachable' : `Up · ${health?.environment ?? ''}`}
                            </span>
                        </div>
                        <div>
                            <span className={styles.healthCheckName}>Client database</span>
                            <span className={health?.checks.leadsDb.ok ? styles.checkGood : styles.checkBad}>
                                {health ? (health.checks.leadsDb.ok ? `OK · ${health.checks.leadsDb.latency_ms}ms` : 'FAILED') : '—'}
                            </span>
                        </div>
                        <div>
                            <span className={styles.healthCheckName}>Lender approval list</span>
                            <span className={health?.checks.funderDb.ok ? styles.checkGood : styles.checkBad}>
                                {health
                                    ? health.checks.funderDb.ok
                                        ? `OK · ${health.checks.funderDb.lenders} lenders`
                                        : 'FAILED'
                                    : '—'}
                            </span>
                        </div>
                        <div>
                            <span className={styles.healthCheckName}>Latency</span>
                            <span>{latencyLabel}</span>
                        </div>
                    </div>
                    <div className={styles.healthHistory}>
                        {history.map((s, i) => (
                            <span
                                key={i}
                                className={s.ok ? styles.historyOk : styles.historyBad}
                                title={`${formatTime(s.at)} · ${s.ok ? 'ok' : 'failed'} · ${s.latency}ms`}
                            />
                        ))}
                    </div>
                    <p className={styles.healthFootnote}>
                        Last checked: {lastChecked ? lastChecked.toLocaleTimeString('en-GB') : '—'}
                        {healthError ? ` — ${healthError}` : ''}
                    </p>
                </div>

                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        <h4>Deliveries per day</h4>
                        <div className={styles.rangeButtons}>
                            {[7, 30, 90].map((d) => (
                                <button
                                    key={d}
                                    className={days === d ? styles.rangeActive : ''}
                                    onClick={() => setDays(d)}
                                >
                                    {d}d
                                </button>
                            ))}
                        </div>
                    </div>
                    <p className={styles.chartSub}>
                        Every API request received, split into accepted and rejected. Click a point to see exactly
                        what arrived that day.
                        {stats?.baseline
                            ? ` Counting starts fresh from ${formatDay(stats.baseline.slice(0, 10))} — the integration testing phase before that is excluded (viewable in Data Optics).`
                            : ''}
                    </p>
                    <div className={styles.chartWrap}>
                        <Line data={chartData} options={chartOptions} />
                    </div>
                </div>
            </div>

            <div className={styles.acceptanceCard}>
                <div className={styles.acceptanceHeader}>
                    <div>
                        <h4>How clients are accepted</h4>
                        <p>
                            FFJ delivers signed clients as single JSON payloads. Each delivery passes through
                            six gates in order under an all-or-nothing policy — one failure rejects the entire
                            delivery and nothing is stored. Once a client is fully onboarded, an onboarding webhook
                            fires to Contact Fusion with the client, every claim and their document links.
                        </p>
                    </div>
                    <div className={styles.docLinks}>
                        {DOCUMENTS.map((doc) => (
                            <div key={doc.title} className={styles.docLink}>
                                <div>
                                    <span className={styles.docTitle}>{doc.title}</span>
                                    <span className={styles.docDescription}>{doc.description}</span>
                                </div>
                                <div className={styles.docActions}>
                                    <a href={docUrl(doc.url)} target="_blank" rel="noreferrer">
                                        View
                                    </a>
                                    <button onClick={() => openDocAsPdf(doc.url)}>Save as PDF</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.gatePipeline}>
                    {GATES.map((gate, i) => (
                        <React.Fragment key={gate.name}>
                            <div className={styles.gateStep}>
                                <span className={styles.gateNumber}>{i + 1}</span>
                                <span className={styles.gateName}>{gate.name}</span>
                                <span className={styles.gateDetail}>{gate.detail}</span>
                            </div>
                            {i < GATES.length - 1 && <span className={styles.gateArrow}>→</span>}
                        </React.Fragment>
                    ))}
                </div>

                <div className={styles.criteriaGrid}>
                    {CRITERIA.map((section) => (
                        <div key={section.title} className={styles.criteriaBox}>
                            <h5>{section.title}</h5>
                            <ul>
                                {section.items.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className={styles.lenderSection}>
                    <button className={styles.lenderToggle} onClick={() => setShowLenders((s) => !s)}>
                        {showLenders ? 'Hide' : 'Show'} approved lender list
                        {lenders.length ? ` (${lenders.length})` : ''}
                    </button>
                    {showLenders && (
                        <div className={styles.lenderGrid}>
                            {lenders.map((lender) => (
                                <span key={lender}>{lender}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {dayDetail && (
                <div className={styles.dayDetail}>
                    <div className={styles.dayDetailHeader}>
                        <h4>{formatDay(dayDetail.day)}</h4>
                        <button className={styles.closeButton} onClick={() => setDayDetail(null)}>
                            Close
                        </button>
                    </div>
                    {dayDetail.loading ? (
                        <p>Loading…</p>
                    ) : dayDetail.error ? (
                        <p className={styles.errorText}>{dayDetail.error}</p>
                    ) : dayDetail.rows.length === 0 ? (
                        <p>No live deliveries were received on this day.</p>
                    ) : (
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Client ref</th>
                                    <th>Email</th>
                                    <th>Agreements</th>
                                    <th>Outcome</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {dayDetail.rows.map((row) => (
                                    <tr key={`${row.outcome}-${row.id}`}>
                                        <td>{formatTime(row.created_at)}</td>
                                        <td>{row.consumer_client_ref ?? '—'}</td>
                                        <td>{row.consumer_email ?? '—'}</td>
                                        <td>{row.agreement_count}</td>
                                        <td>
                                            {row.outcome === 'accepted' ? (
                                                <span className={styles.chipAccepted}>
                                                    {STATUS_LABELS[row.status ?? ''] ?? row.status}
                                                </span>
                                            ) : (
                                                <span className={styles.chipRejected}>
                                                    {REASON_LABELS[row.reason_category ?? ''] ?? 'Rejected'}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                className={styles.linkButton}
                                                onClick={() =>
                                                    navigate(
                                                        `/ffj-api/optics?view=all&record=${row.outcome}-${row.id}`
                                                    )
                                                }
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    )
}
